import { listAdminApps, type AdminApp } from '@/features/admin/apps/api'
import { listModelDistributionKeys } from '@/features/admin/model-keys/api'
import { listRegisteredModels } from '@/features/admin/model-provider/api'
import { listMemberAccounts } from '@/features/admin/organization/api'
import {
  getAdminSsoConfig,
  getBrandingSettings,
  getCoreAgentSettings,
} from '@/features/admin/settings/api'

const pendingReviewStatuses = ['draft', 'testing', 'pending_publish'] as const
const expiringKeyWindowDays = 14

export interface AdminOverviewRecentApp {
  id: string
  name: string
  status: string
  updatedAt: string
}

export interface AdminOverviewData {
  activeDistributionKeys: number
  activeMembers: number
  activeModels: number
  brandingComplete: boolean
  coreAgentEnabled: boolean
  enabledQuotaRules: number
  expiringDistributionKeys: number
  pendingReviewApps: number
  recentApps: AdminOverviewRecentApp[]
  ssoConfigured: boolean
}

function isWithinDays(value: string | null | undefined, days: number) {
  if (!value) {
    return false
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return false
  }

  const now = Date.now()
  const upperBound = now + days * 24 * 60 * 60 * 1000

  return date.getTime() >= now && date.getTime() <= upperBound
}

function recentApp(app: AdminApp): AdminOverviewRecentApp {
  return {
    id: app.id,
    name: app.name,
    status: app.status,
    updatedAt: app.updated_at,
  }
}

export async function getAdminOverview() {
  const [
    pendingApps,
    recentApps,
    activeMembers,
    activeModels,
    activeKeys,
    ssoConfig,
    coreAgentSettings,
    brandingSettings,
  ] = await Promise.all([
    listAdminApps({
      order: 'desc',
      orderBy: 'updated_at',
      page: 1,
      pageSize: 1,
      status: [...pendingReviewStatuses],
    }),
    listAdminApps({
      order: 'desc',
      orderBy: 'updated_at',
      page: 1,
      pageSize: 5,
    }),
    listMemberAccounts({
      order: 'desc',
      orderBy: 'created_at',
      page: 1,
      pageSize: 1,
      status: 'active',
    }),
    listRegisteredModels({
      page: 1,
      pageSize: 1,
      status: 'active',
    }),
    listModelDistributionKeys({
      page: 1,
      pageSize: 200,
      status: 'active',
    }),
    getAdminSsoConfig(),
    getCoreAgentSettings(),
    getBrandingSettings(),
  ])

  const activeKeyItems = activeKeys.items
  const expiringDistributionKeys = activeKeyItems.filter((key) =>
    isWithinDays(key.expires_at, expiringKeyWindowDays),
  ).length
  const enabledQuotaRules = activeKeyItems.reduce(
    (total, key) => total + (key.quota_rules ?? []).filter((rule) => rule.enabled !== false).length,
    0,
  )
  const title = brandingSettings.config.title?.trim() ?? ''

  return {
    activeDistributionKeys: activeKeys.total,
    activeMembers: activeMembers.total,
    activeModels: activeModels.total,
    brandingComplete: title.length > 0,
    coreAgentEnabled: coreAgentSettings.enabled,
    enabledQuotaRules,
    expiringDistributionKeys,
    pendingReviewApps: pendingApps.total,
    recentApps: recentApps.items.map(recentApp),
    ssoConfigured: Boolean(ssoConfig.web_sso_enabled && ssoConfig.sso_type),
  } satisfies AdminOverviewData
}
